import FrameworkUtils from 'utils/framework';
import { Image, Flex, Box } from 'components/ui';
import {
    space, fontSizes, fontWeights, mediaQueries, breakpoints, colors, gradients
} from 'style/config';

import { ENTRYPOINTS } from 'ai/constants/superchat';
import { useEffect, useRef, useState } from 'react';
import Location from 'utils/Location';
import { AI_CHAT_BLACK_IMAGE } from 'ai/utils/imagePaths';
import { isGenAINewUIEnabledWithRCPS } from 'ai/utils/aiBeautyChat';
const { wrapFunctionalComponent } = FrameworkUtils;

function MinimizedSuperChat(props) {
    const aiBeautyChatRef = useRef(null);
    const {
        showSuperChat, localization, session, config, location, contextPrompts, isGladlyOpen
    } = props;
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(false);

    const checkIsMobile = () => {
        setIsMobile(window.matchMedia(breakpoints.smMax).matches);
    };

    const basketCheckoutHeightSmui = isBasketPage => {
        const dataHeightKey = isBasketPage ? 'bottomCostsummaryHeight' : 'bottomStickyHeight';

        return document.body.querySelector(`[data-height="${dataHeightKey}"]`)?.clientHeight || 0;
    };

    const updateOffset = (element, gladlyOpen) => {
        if (!element) {
            return;
        }

        requestAnimationFrame(() => {
            const isBasketPage = Location.isBasketPage();
            const height = basketCheckoutHeightSmui(isBasketPage);
            const gladlyButtonOffset = gladlyOpen ? 48 + space[2] : 0;

            element.style.setProperty('--custom-bottom-offset', `calc(var(--bottomNavHeight, 0px) + ${height + space[2] + gladlyButtonOffset}px)`);
            setLoading(false);
        });
    };

    const setRef = element => {
        aiBeautyChatRef.current = element;

        if (element) {
            updateOffset(element, isGladlyOpen);
        }
    };

    useEffect(() => {
        if (aiBeautyChatRef.current) {
            updateOffset(aiBeautyChatRef.current, isGladlyOpen);
        }
    }, [session, location, isGladlyOpen]);

    useEffect(() => {
        checkIsMobile();

        const handleResize = () => {
            checkIsMobile();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    if (showSuperChat || !session) {
        return null;
    }

    // if isGenAINewUIEnabledWithRCPS and mobile - we show bottom nav - hide floating icon
    const isGenAINewUIEnabled = isGenAINewUIEnabledWithRCPS();

    if (isGenAINewUIEnabled && isMobile) {
        return null;
    }

    const openSuperChat = () => {
        // Create a copy of the session without selectedPrompt to prevent auto-sending on reinitialization
        const sessionWithoutPrompt = { ...session };
        delete sessionWithoutPrompt.selectedPrompt;
        delete sessionWithoutPrompt.source;
        sessionWithoutPrompt.entrypoint = sessionWithoutPrompt.entrypoint ?? ENTRYPOINTS.DEFAULT;

        props.openSuperChat(sessionWithoutPrompt, contextPrompts);
    };

    return (
        <Flex
            css={styles.floatingContainer(isGenAINewUIEnabled, isGladlyOpen)}
            onClick={openSuperChat}
            ref={setRef}
            display={loading ? 'none' : 'flex'}
        >
            <Image
                disableLazyLoad={true}
                alt={config?.meta?.bot_name || localization.title}
                src={isGenAINewUIEnabled ? AI_CHAT_BLACK_IMAGE : '/img/ufe/ai/ai_chat_white.svg'}
                width={22}
                height={22}
                css={styles.chatImg}
            />
            <Box css={styles.chatText(isGenAINewUIEnabled)}>{config?.meta?.bot_name || localization.title}</Box>
            <Flex
                width={24}
                alignItems='center'
                justifyContent='center'
                css={styles.chevronIcon}
            >
                <Image
                    disableLazyLoad={true}
                    alt='Up Chevron'
                    src={
                        isGenAINewUIEnabled
                            ? '/img/ufe/ai/minimized-super-chat/up-chevron-black.svg'
                            : '/img/ufe/ai/minimized-super-chat/up-chevron.svg'
                    }
                    width='1em'
                    height='1em'
                />
            </Flex>
        </Flex>
    );
}

const styles = {
    floatingContainer: (isGenAINewUIEnabled, gladlyVisible) => ({
        position: 'fixed',
        bottom: gladlyVisible ? space[4] + 48 + space[2] : space[4],
        right: space[4],
        width: 258,
        height: 48,
        background: isGenAINewUIEnabled ? gradients.aiBeautyChat : colors.black,
        borderRadius: 100,
        zIndex: 1000,
        padding: `${space[3]}px ${space[4]}px`,
        gap: space[2],
        alignItems: 'center',
        transition: 'bottom 0.3s ease-in-out'
    }),
    chatText: isGenAINewUIEnabled => ({
        [mediaQueries.smMax]: {
            display: 'none'
        },
        color: isGenAINewUIEnabled ? colors.black : colors.white,
        fontSize: fontSizes.md,
        fontWeight: fontWeights.bold,
        flex: 1
    }),
    chevronIcon: {
        [mediaQueries.smMax]: {
            display: 'none'
        }
    }
};

export default wrapFunctionalComponent(MinimizedSuperChat, 'MinimizedSuperChat');
